import React from 'react';
import { Github, ExternalLink } from 'lucide-react';

interface FooterSection {
  uid: string;
  section_title: string;
  section_id: string;
  section_position: 'left' | 'center' | 'right';
  content_items?: Array<{
    item_text: string;
    item_link?: string;
  }>;
  rich_content?: string;
  sort_order: number;
  is_active: boolean;
}

const getDefaultFooterSections = (): FooterSection[] => [
    {
      uid: 'default-about',
      section_title: 'About Perfect Weather',
      section_id: 'footer_about',
      section_position: 'left',
      rich_content: 'Discover locations with weather patterns that match your preferences using real historical climate data from NOAA\'s Global Summary of the Day dataset.',
      sort_order: 1,
      is_active: true
    },
    {
      uid: 'default-data',
      section_title: 'Data Sources',
      section_id: 'footer_data_sources',
      section_position: 'center',
      content_items: [
        { item_text: 'NOAA GSOD Dataset (2018-2023)' },
        { item_text: 'Google BigQuery Processing' },
        { item_text: 'Static Content Management' }
      ],
      sort_order: 2,
      is_active: true
    },
    {
      uid: 'default-tech',
      section_title: 'Technology Stack',
      section_id: 'footer_tech_stack',
      section_position: 'right',
      content_items: [
        { item_text: 'Built with React, TypeScript, and Tailwind CSS' },
        { item_text: 'Serverless backend powered by Google Cloud Run' },
        { item_text: 'Streamlined content management' }
      ],
      sort_order: 3,
      is_active: true
    }
  ];

export const FlexibleFooter: React.FC = () => {
  const footerSections = getDefaultFooterSections();
  const appConfig = {
    app_name: 'Perfect Weather',
    contact_email: 'support@perfectweather.app',
    social_links: {
      github: 'https://github.com/your-username/perfectweather'
    }
  };

  const renderSection = (section: FooterSection) => {
    return (
      <div key={section.uid}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
          {section.section_title}
        </h3>
        
        {/* Render rich content (paragraph style) */}
        {section.rich_content && (
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {section.rich_content}
          </p>
        )}
        
        {/* Render content items (list style) */}
        {section.content_items && section.content_items.length > 0 && (
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            {section.content_items.map((item, index) => (
              <li key={index} className="flex items-start">
                {item.item_link ? (
                  <a
                    href={item.item_link}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1"
                    target={item.item_link.startsWith('http') ? '_blank' : '_self'}
                    rel={item.item_link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {item.item_text}
                    {item.item_link.startsWith('http') && (
                      <ExternalLink className="h-3 w-3" />
                    )}
                  </a>
                ) : (
                  <span>{item.item_text}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // Group sections by position
  const sectionsByPosition = footerSections.reduce((acc, section) => {
    if (!acc[section.section_position]) {
      acc[section.section_position] = [];
    }
    acc[section.section_position].push(section);
    return acc;
  }, {} as Record<string, FooterSection[]>);

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-16">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {sectionsByPosition.left?.map(renderSection)}
            {/* Add contact info if available */}
            {appConfig?.contact_email && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                Contact: <a 
                  href={`mailto:${appConfig.contact_email}`}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  {appConfig.contact_email}
                </a>
              </p>
            )}
          </div>
          
          {/* Center Column */}
          <div className="space-y-6">
            {sectionsByPosition.center?.map(renderSection)}
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            {sectionsByPosition.right?.map(renderSection)}
            
            {/* Social Links */}
            {appConfig?.social_links && (
              <div className="flex space-x-4 mt-4">
                {appConfig.social_links.github && (
                  <a
                    href={appConfig.social_links.github}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                  >
                    <Github className="h-5 w-5" />
                  </a>
                )}
                {/* Add other social links as needed */}
              </div>
            )}
          </div>
        </div>
        
        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} {appConfig?.app_name || 'Perfect Weather'}. 
            All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
